import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
`;

const WelcomeText = styled.h1`
  color: #333;
  font-size: 2rem;
  font-weight: 600;
`;

const LogoutButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #c82333;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const Card = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f3f4;
  
  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  font-weight: 500;
  color: #666;
`;

const Value = styled.span`
  font-weight: 600;
  color: #333;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  
  &.active {
    background: #d4edda;
    color: #155724;
  }
  
  &.inactive {
    background: #f8d7da;
    color: #721c24;
  }
`;

const PaymentTable = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
`;

const TableTitle = styled.h2`
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  font-weight: 600;
  color: #666;
  border-bottom: 1px solid #e9ecef;
`;

const Td = styled.td`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f3f4;
  color: #333;
`;

const PaymentStatus = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  
  &.paid {
    background: #d4edda;
    color: #155724;
  }
  
  &.pending {
    background: #fff3cd;
    color: #856404;
  }
  
  &.over-paid {
    background: #cce5ff;
    color: #004085;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const CheckoutCard = styled(Card)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin-bottom: 2rem;
  border: none;
`;

const CheckoutTitle = styled.h3`
  color: white;
  margin: 0 0 1rem 0;
  font-size: 1.3rem;
`;

const CheckoutLink = styled.a`
  display: inline-block;
  background: white;
  color: #667eea;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  margin-top: 1rem;
  word-break: break-all;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    background: #f8f9fa;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CheckoutMessage = styled.p`
  color: white;
  margin: 0.5rem 0;
  opacity: 0.9;
`;

const Dashboard = ({ user, onLogout }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewalLoading, setRenewalLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/payments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRenewal = async () => {
    setRenewalLoading(true);
    setCheckoutUrl(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/create-renewal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const url = data.checkout_url;
        
        // Store the URL in state to display it
        setCheckoutUrl(url);
        
        // Try to open the URL automatically
        const newWindow = window.open(url, '_blank');
        
        // Check if popup was blocked (mobile Safari often blocks this)
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Popup was blocked, show alert with URL
          alert(`Invoice created! Please click the link below to complete payment:\n\n${url}`);
        }
      } else {
        alert('Failed to create renewal invoice. Please try again.');
      }
    } catch (error) {
      console.error('Error creating renewal:', error);
      alert('Error creating renewal invoice. Please try again.');
    } finally {
      setRenewalLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    onLogout();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getPlanDisplayName = (planType) => {
    const planNames = {
      'core': 'Core Plan',
      'growth': 'Growth Plan',
      'enterprise': 'Enterprise Plan',
      'ultimate': 'Ultimate Plan',
      'ultamega': 'Ulta Mega Plan'
    };
    return planNames[planType] || planType;
  };

  const getApiLimitDisplay = (limit) => {
    if (limit === -1) return 'Unlimited';
    return `${limit.toLocaleString()} calls/month`;
  };

  const getDaysRemaining = () => {
    if (!user.subscription_end) return null;
    const days = Math.ceil((new Date(user.subscription_end) - new Date()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const getSubscriptionStatus = () => {
    const daysRemaining = getDaysRemaining();
    
    // Check if user has a recent paid payment
    const hasRecentPaidPayment = payments.some(payment => 
      payment.status === 'paid' || payment.status === 'Over Paid'
    );
    
    if (!user.is_active) {
      return { status: 'expired', message: 'Subscription has expired', color: 'red' };
    }
    
    // If user has a recent paid payment but subscription_end hasn't been updated yet
    if (hasRecentPaidPayment && daysRemaining <= 0) {
      return { status: 'processing', message: 'Payment received - subscription being updated', color: 'blue' };
    }
    
    // If subscription is active and has more than 7 days remaining
    if (daysRemaining > 7) {
      return { status: 'active', message: `Subscription expires in ${daysRemaining} days`, color: 'green' };
    }
    
    if (daysRemaining === 0) {
      return { status: 'expiring', message: 'Subscription expires today', color: 'orange' };
    }
    
    if (daysRemaining <= 3) {
      return { status: 'warning', message: `Subscription expires in ${daysRemaining} days`, color: 'orange' };
    }
    
    if (daysRemaining <= 7) {
      return { status: 'notice', message: `Subscription expires in ${daysRemaining} days`, color: 'blue' };
    }
    
    return { status: 'active', message: `Subscription expires in ${daysRemaining} days`, color: 'green' };
  };

  const subscriptionStatus = getSubscriptionStatus();

  return (
    <DashboardContainer>
      <Header>
        <WelcomeText>Welcome back, {user.username}!</WelcomeText>
        <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
      </Header>

      {/* Checkout URL Display */}
      {checkoutUrl && (
        <CheckoutCard>
          <CheckoutTitle>💳 Payment Invoice Ready</CheckoutTitle>
          <CheckoutMessage>
            Your invoice has been created. Click the link below to complete your payment:
          </CheckoutMessage>
          <CheckoutLink 
            href={checkoutUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={() => setCheckoutUrl(null)}
          >
            Open Payment Page
          </CheckoutLink>
          <CheckoutMessage style={{ fontSize: '0.85rem', marginTop: '1rem', opacity: 0.8 }}>
            Or copy this URL: {checkoutUrl}
          </CheckoutMessage>
        </CheckoutCard>
      )}

      {/* Subscription Status Warning */}
      {subscriptionStatus.status !== 'active' && (
        <Card style={{ marginBottom: '2rem', border: `2px solid ${subscriptionStatus.color}` }}>
          <CardTitle style={{ color: subscriptionStatus.color }}>
            {subscriptionStatus.status === 'processing' ? '🔄' : '⚠️'} {subscriptionStatus.message}
          </CardTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ margin: 0, color: '#666' }}>
              {subscriptionStatus.status === 'processing' 
                ? 'Your payment has been received and your subscription is being updated. This may take a few minutes.'
                : 'To continue using QuantDelta services, please renew your subscription.'
              }
            </p>
            {subscriptionStatus.status !== 'processing' && (
              <button 
                onClick={handleRenewal}
                disabled={renewalLoading}
                style={{ 
                  background: subscriptionStatus.color,
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                {renewalLoading ? 'Creating...' : 'Renew Subscription'}
              </button>
            )}
          </div>
        </Card>
      )}

      <Grid>
        <Card>
          <CardTitle>📊 Subscription Status</CardTitle>
          <InfoRow>
            <Label>Status</Label>
            <StatusBadge className={user.is_active ? 'active' : 'inactive'}>
              {user.is_active ? 'Active' : 'Inactive'}
            </StatusBadge>
          </InfoRow>
          <InfoRow>
            <Label>Plan</Label>
            <Value>{getPlanDisplayName(user.subscription_type)}</Value>
          </InfoRow>
          <InfoRow>
            <Label>API Calls Used</Label>
            <Value>{user.api_calls_used.toLocaleString()} / {getApiLimitDisplay(user.api_calls_limit)}</Value>
          </InfoRow>
        </Card>

        <Card>
          <CardTitle>📅 Billing Information</CardTitle>
          <InfoRow>
            <Label>Current Period Start</Label>
            <Value>{user.subscription_start ? formatDate(user.subscription_start) : 'N/A'}</Value>
          </InfoRow>
          <InfoRow>
            <Label>Next Invoice Date</Label>
            <Value>{user.subscription_end ? formatDate(user.subscription_end) : 'N/A'}</Value>
          </InfoRow>
          <InfoRow>
            <Label>Days Remaining</Label>
            <Value style={{ color: subscriptionStatus.color }}>
              {getDaysRemaining() !== null ? `${getDaysRemaining()} days` : 'N/A'}
            </Value>
          </InfoRow>
        </Card>
      </Grid>

      <PaymentTable>
        <TableHeader>
          <TableTitle>💰 Payment History</TableTitle>
        </TableHeader>
        
        {loading ? (
          <EmptyState>Loading payment history...</EmptyState>
        ) : payments.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Plan</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>Invoice ID</Th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <Td>{formatDate(payment.created_at)}</Td>
                  <Td>{getPlanDisplayName(payment.plan_type)}</Td>
                  <Td>{formatCurrency(payment.amount_usd)}</Td>
                  <Td>
                    <PaymentStatus className={payment.status.toLowerCase().replace(' ', '-')}>
                      {payment.status}
                    </PaymentStatus>
                  </Td>
                  <Td>{payment.invoice_id}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>No payment history found.</EmptyState>
        )}
      </PaymentTable>
    </DashboardContainer>
  );
};

export default Dashboard; 